/**
 * Auth Store
 *
 * Zustand store for managing authentication state.
 * Provides user information, loading states, and auth actions.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '../../../types/electron';
import * as authService from '../services/authService';
import { useLicenseStore } from './licenseStore';
import { fetchEntitlementToken } from '../services/entitlementTokenService';
import { isAuthConfigured } from '../services/authService';
import {
  consumeAuthIntentSource,
  recordPostSignInTrialOffer,
  shouldOfferTrialAfterSignIn,
} from '../services/signInIntent';
import { createGuardedJsonStorage } from '../../storage/guardedJsonStorage';

/**
 * Track auth analytics event from store (non-React context).
 * Fire-and-forget pattern - analytics errors are silently ignored.
 */
function trackAuthEvent(eventName: string, props: Record<string, string> = {}): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  window.electronAPI.analytics.track(eventName, props).catch(() => {
    // Silently ignore analytics errors
  });
}

/**
 * Restore entitlement (trial/subscription) state from server after sign-in.
 * This ensures that when a user signs back in, their existing trial is restored.
 * Fire-and-forget pattern - errors are logged but don't block sign-in.
 *
 * While this runs we flip `isResolvingEntitlement` so UI surfaces that gate
 * Personal-vs-Pro rendering (e.g. SessionsDialog) can wait for a definitive
 * answer instead of flashing the Personal layout first.
 */
async function restoreEntitlementFromServer(userId?: string): Promise<void> {
  // Skip if auth backend is not configured
  if (!isAuthConfigured()) return;

  useAuthStore.setState({ isResolvingEntitlement: true });

  try {
    const result = await fetchEntitlementToken();

    if (!result.success || !result.payload) {
      console.log('[AuthStore] No entitlement to restore:', result.error);
      return;
    }

    const { payload } = result;

    // Restore trial state if user has an active trial on the server
    if (payload.mode === 'trial' && payload.trial_started_at && payload.trial_expires_at) {
      console.log('[AuthStore] Restoring server trial for user:', userId);
      useLicenseStore.getState().startTrialFromServer(
        new Date(payload.trial_started_at),
        new Date(payload.trial_expires_at),
        userId
      );

      // Cache the signed token for offline use
      if (result.token && window.electronAPI?.licenseCache) {
        try {
          await window.electronAPI.licenseCache.updateCacheWithSignedToken(result.token);
        } catch (cacheErr) {
          console.warn('[AuthStore] Failed to cache entitlement token:', cacheErr);
        }
      }
    }
  } catch (err) {
    console.error('[AuthStore] Failed to restore entitlement:', err);
    // Don't throw - sign-in should still succeed
  } finally {
    useAuthStore.setState({ isResolvingEntitlement: false });
  }
}

/**
 * Dedupes concurrent `initialize()` calls.
 *
 * Several components call `useAuth()` (which calls `store.initialize()` on
 * mount) at app boot — e.g. `useDeviceRegistration` and the always-mounted
 * `<AuthDialog>` in App.tsx. Their mount-effects run in the same commit,
 * before the first call's `await authService.initialize()` has a chance to
 * flip `isInitialized`, so the `if (get().isInitialized) return` guard alone
 * doesn't stop concurrent callers. Without this, each concurrent caller
 * would register its own (never unsubscribed) `onAuthStateChanged` IPC
 * listener, so a single real SIGNED_IN event fires the listener N times —
 * inflating 'auth:sign_in' analytics and re-running entitlement restore N
 * times per event. Sharing one in-flight promise makes the whole init body
 * (including the listener registration) run exactly once.
 */
let authInitPromise: Promise<void> | null = null;

/**
 * Auth state persisted across sessions.
 * We only persist minimal user info, not the full session.
 */
interface PersistedAuthState {
  /** User email (for display purposes) */
  userEmail?: string;
  /** User ID */
  userId?: string;
}

/**
 * Result of signup operation.
 * Separate from login because signup requires email confirmation.
 */
export interface SignUpResult {
  success: boolean;
  /** True if signup succeeded and email confirmation is required */
  emailConfirmationRequired: boolean;
  /** Error message if signup failed */
  error?: string;
}

interface AuthStore {
  // State
  user: AuthUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  /**
   * True while we're fetching the user's entitlement token from the server
   * after auth init / sign-in. Use this to avoid flashing Personal-mode UI
   * before the trial/subscription state is known.
   */
  isResolvingEntitlement: boolean;
  error?: string;

  // Actions
  initialize: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signInWithOAuth: (provider: authService.OAuthProvider) => Promise<boolean>;
  signOut: () => Promise<boolean>;
  setUser: (user: AuthUser | null) => void;
  setError: (error?: string) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isLoading: false,
      isInitialized: false,
      isResolvingEntitlement: false,
      error: undefined,

      // Initialize auth state on app start.
      // Dedupes concurrent calls via `authInitPromise` — see its doc comment.
      initialize: async () => {
        if (get().isInitialized) return;
        if (authInitPromise) return authInitPromise;

        authInitPromise = (async () => {
          set({ isLoading: true });

          try {
            // Try to restore session from stored tokens
            const user = await authService.initialize();

            // Set up IPC listener for auth state changes
            window.electronAPI?.auth?.onAuthStateChanged?.((data) => {
              if (data.event === 'SIGNED_IN' && data.user) {
                const store = useAuthStore.getState();
                store.setUser(data.user);
                // `source` = which surface opened the sign-in flow (persisted
                // by signInIntent so it survives the external-browser round
                // trip and even an app restart before the deep link returns).
                const source = consumeAuthIntentSource();
                trackAuthEvent('auth:sign_in', { source });
                // A completed sign-in owes the user one trial offer if the
                // account resolves to no Pro — whichever surface opened it
                // (useTrialOnboarding consumes this once entitlement is known).
                if (shouldOfferTrialAfterSignIn(source)) {
                  recordPostSignInTrialOffer();
                }
                restoreEntitlementFromServer(data.user.id);
              } else if (data.event === 'SIGNED_OUT') {
                useAuthStore.getState().setUser(null);
              } else if (data.event === 'TOKEN_REFRESHED' && data.user) {
                useAuthStore.getState().setUser(data.user);
              }
            });

            // If we restored a session we'll also resolve entitlement —
            // flip the flag in the same set() so consumers don't see a brief
            // "auth ready, entitlement unknown" window between renders.
            set({
              user,
              isInitialized: true,
              isLoading: false,
              isResolvingEntitlement: Boolean(user) && isAuthConfigured(),
            });

            if (user) {
              restoreEntitlementFromServer(user.id);
            }
          } catch {
            set({
              user: null,
              isInitialized: true,
              isLoading: false,
            });
          }
        })();

        return authInitPromise;
      },

      // Sign up with email/password
      // With LogTo, this opens the LogTo sign-in page where users can register
      signUp: async (_email: string, _password: string): Promise<SignUpResult> => {
        set({ isLoading: true, error: undefined });

        const result = await authService.signIn();

        if (result.success) {
          // Sign-in flow initiated — completion comes via IPC callback
          // Don't set isLoading to false yet
          return { success: true, emailConfirmationRequired: false };
        }

        set({ isLoading: false, error: result.error });
        return { success: false, emailConfirmationRequired: false, error: result.error };
      },

      // Sign in with email/password
      // With LogTo, this opens the LogTo sign-in page
      signIn: async (_email: string, _password: string) => {
        set({ isLoading: true, error: undefined });

        const result = await authService.signIn();

        if (result.success) {
          // Sign-in flow initiated — completion comes via IPC callback
          return true;
        }

        set({ isLoading: false, error: result.error });
        return false;
      },

      // Sign in with OAuth provider
      signInWithOAuth: async (provider: authService.OAuthProvider) => {
        set({ isLoading: true, error: undefined });

        const result = await authService.signInWithOAuth(provider);

        if (result.success) {
          // OAuth flow initiated — completion comes via IPC callback
          return true;
        }

        set({ isLoading: false, error: result.error });
        return false;
      },

      // Sign out
      signOut: async () => {
        set({ isLoading: true, error: undefined });

        const result = await authService.signOut();

        if (result.success) {
          // Clear the offline license cache on logout
          try {
            await window.electronAPI?.licenseCache?.clearCache();
          } catch (e) {
            console.error('Failed to clear license cache on logout:', e);
          }

          // Clear the main process entitlement on logout
          try {
            await window.electronAPI?.entitlement?.clear();
          } catch (e) {
            console.error('Failed to clear entitlement on logout:', e);
          }

          // Clear trial state on logout (ACCOUNT-BASED: trial is tied to user account)
          useLicenseStore.getState().clearTrialState();

          set({
            user: null,
            isLoading: false,
          });
          trackAuthEvent('auth:sign_out');
          return true;
        }

        set({ isLoading: false, error: result.error });
        return false;
      },

      // Set user directly (used by auth state change listener)
      setUser: (user: AuthUser | null) => {
        set({ user, isLoading: false });
      },

      // Set error
      setError: (error?: string) => {
        set({ error });
      },

      // Clear error
      clearError: () => {
        set({ error: undefined });
      },
    }),
    {
      name: 'sql-studio-auth',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — it hydrates before the toaster mounts, and the cached email is
      // re-read from the auth service; the drop is still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      partialize: (state): PersistedAuthState => ({
        // Only persist minimal user info for display
        userEmail: state.user?.email,
        userId: state.user?.id,
      }),
    }
  )
);
